/**
 * El mapa del Estudio de marketing, DECLARADO COMO DATOS.
 *
 * `CircuitoMapa.tsx` solo sabe renderizar esta estructura: no conoce ningún
 * nombre de agente. Sumar el explorador o partir producción en dos es editar
 * este archivo (ADR-2026-08-16-estudio-marketing-mapa-grafo-declarativo).
 */

/** Un punto en coordenadas del viewBox del mapa. */
export interface Punto {
  x: number;
  y: number;
}

export type RolNodo = "fuente" | "agente" | "compuerta" | "humano" | "memoria" | "global";
/**
 * `desconocido` NO es un cuarto sinónimo de "apagado": es la ausencia de
 * respuesta. Existe porque el mapa pintaba de `sin-conectar` a una fuente cuya
 * consulta se cayó, que es una AFIRMACIÓN sobre la cuenta del usuario ("esto
 * está desconectado") hecha sin ningún dato — y encima la pantalla a la que
 * ahora lleva el nodo dice lo contrario ("No pudimos consultarlo"). Es la misma
 * regla que fija `datos/estadoFuente.ts`, ahora también del lado del dibujo
 * (ADR-2026-08-17-estudio-marketing-fixes-revision).
 */
export type EstadoNodo = "activo" | "sin-conectar" | "no-existe" | "desconocido";

export interface NodoGrafo {
  id: string;
  etiqueta: string;
  /** Segunda línea de la etiqueta, opcional. */
  sub?: string;
  rol: RolNodo;
  estado: EstadoNodo;
  posicion: { tipo: "anillo"; grados: number } | { tipo: "libre"; x: number; y: number };
  radio: number;
  /** Ruta a la que navega. `null` = no clickeable. */
  destino: string | null;
  glifo: GlifoNodo;
  /** Dónde va la etiqueta respecto del círculo. */
  etiquetaEn?: "arriba" | "abajo" | "izquierda" | "derecha";
}

export type GlifoNodo =
  | "datos" | "estratega" | "creativo" | "ia" | "calidad"
  | "armado" | "humano" | "copy" | "memoria" | "publicacion";

export interface AristaGrafo {
  desde: string;
  hasta: string;
  /**
   * Hubo un quinto tipo, `escritura`: la vuelta de `estratega` a `personas`.
   * Se borró junto con su arista — era la única — porque el vínculo se lee de
   * una sola línea recta (ADR-2026-08-17-mapa-orden-y-senales).
   */
  tipo: "flujo" | "retorno" | "lectura" | "fuera-de-camino";
  /**
   * La arista no termina en un nodo sino en un PUNTO DEL ANILLO: el del tramo
   * que va de `hasta` a `tramo.hacia`, a la fracción `tramo.en` (0 = sobre
   * `hasta`, 1 = sobre `tramo.hacia`). Sirve para colgar algo de una etapa del
   * circuito y no de un agente puntual. Ambos extremos del tramo tienen que
   * estar sobre el anillo — `validarGrafo` lo exige.
   */
  tramo?: { hacia: string; en: number };
}

export const ANILLO = { cx: 560, cy: 240, rx: 215, ry: 140 } as const;
export const VIEWBOX = "0 0 900 570";

/**
 * La pieza de ejemplo a la que llevan los nodos de agente: cada uno produce
 * una etapa del brief, así que el clic cae en la pieza que ese agente escribió
 * y no en la lista. `pza_8f21` es el `pieza_id` de `BRIEF_EJEMPLO`
 * (`datos/ejemplo.ts`) — el único brief que hay en el Bloque A. Se deja como
 * literal para que este archivo siga siendo datos puros, sin importar los
 * datos de ejemplo.
 */
export const RUTA_PIEZA_EJEMPLO = "/estudio-marketing/plan/pza_8f21";

/**
 * La pantalla de configuración de datos: dónde se conectan las fuentes y qué
 * lee Atlas además de la cuenta. Es el destino de los tres nodos de fuente
 * (ADR-2026-08-17-estudio-marketing-nodos-fuente-a-datos).
 */
export const RUTA_DATOS = "/estudio-marketing/datos";

export const RUTAS_VALIDAS = [
  "/estudio-marketing/mapa",
  "/estudio-marketing/plan",
  RUTA_PIEZA_EJEMPLO,
  "/estudio-marketing/insights",
  RUTA_DATOS,
  "/estudio-marketing/estrategia",
  "/estudio-marketing/marca",
  "/mi-marca",
];

export function posicionDe(nodo: NodoGrafo): Punto {
  if (nodo.posicion.tipo === "libre") {
    return { x: nodo.posicion.x, y: nodo.posicion.y };
  }
  const rad = (nodo.posicion.grados * Math.PI) / 180;
  return {
    x: ANILLO.cx + ANILLO.rx * Math.cos(rad),
    y: ANILLO.cy + ANILLO.ry * Math.sin(rad),
  };
}

/** Orden del circuito, en sentido antihorario visual desde arriba a la izquierda. */
export const NODOS: NodoGrafo[] = [
  // ── el circuito ──
  // Los siete del anillo comparten radio 26 y estilo: el circuito se lee como
  // una sola pieza y ninguna etapa pesa más que otra
  // (ADR-2026-08-16-mapa-anillo-uniforme-por-posicion).
  { id: "numeros", etiqueta: "Métricas de tu marca", rol: "fuente", estado: "activo",
    posicion: { tipo: "anillo", grados: 250 }, radio: 26,
    destino: "/estudio-marketing/insights", glifo: "datos", etiquetaEn: "abajo" },
  // Etiqueta HACIA ADENTRO del anillo: `estratega` está en el flanco izquierdo
  // (190°), donde la elipse es casi vertical y pasa a 1,5 unidades del centro
  // del nodo. Con la etiqueta "abajo" el trazo del anillo —y el punteado azul
  // animado que corre encima— le entraba por el medio a la palabra. "izquierda"
  // tampoco sirve: ahí cae el abanico de lecturas y la de `estrategia_mem` la
  // cruza de punta a punta. Ver ADR-2026-08-17-mapa-etiquetas-anillo-sin-cruce.
  { id: "estratega", etiqueta: "Estratega", rol: "agente", estado: "activo",
    posicion: { tipo: "anillo", grados: 190 }, radio: 26,
    destino: "/estudio-marketing/plan", glifo: "estratega", etiquetaEn: "derecha" },
  // Los cinco que escriben una etapa del brief van a la pieza, no a la lista.
  { id: "creativo", etiqueta: "Dir. creativo", rol: "agente", estado: "activo",
    posicion: { tipo: "anillo", grados: 130 }, radio: 26,
    destino: RUTA_PIEZA_EJEMPLO, glifo: "creativo", etiquetaEn: "abajo" },
  { id: "dir_ia", etiqueta: "Dir. de IA", rol: "agente", estado: "activo",
    posicion: { tipo: "anillo", grados: 90 }, radio: 26,
    destino: RUTA_PIEZA_EJEMPLO, glifo: "ia", etiquetaEn: "abajo" },
  // PRIMERO SE CREA, DESPUÉS SE VERIFICA. `armado` va en los 50° y `calidad`
  // en los 10°: el control de calidad revisa algo que ya existe, así que no
  // puede estar antes que la creación
  // (ADR-2026-08-17-mapa-orden-y-senales).
  // Etiqueta "abajo": a 50° el anillo corre en diagonal y ya bajó de la altura
  // del texto (el punto más bajo de la elipse es y=380 y la etiqueta cae en
  // y≈389), así que el trazo no la cruza. Es la posición en la que venía
  // `calidad`, con una etiqueta apenas más larga.
  { id: "armado", etiqueta: "Creación de contenido", rol: "agente", estado: "activo",
    posicion: { tipo: "anillo", grados: 50 }, radio: 26,
    destino: RUTA_PIEZA_EJEMPLO, glifo: "armado", etiquetaEn: "abajo" },
  // El nodo de 10° cae en el flanco derecho (x≈772), donde la elipse corre casi
  // vertical, así que ninguno de los cuatro lados sale gratis: "derecha" manda
  // el texto contra el borde del viewBox (900) y lo recorta el `<svg>`;
  // "izquierda" (lo que decidió ADR-2026-08-17-mapa-etiquetas-anillo-sin-cruce,
  // cuando en esta posición estaba `armado`) lo manda hacia adentro del anillo;
  // "abajo" lo apoya sobre el trazo, que a la altura de la etiqueta ya volvió
  // hacia adentro (x≈753 en y=301).
  { id: "calidad", etiqueta: "Control de calidad", rol: "compuerta", estado: "activo",
    posicion: { tipo: "anillo", grados: 10 }, radio: 26,
    // "abajo" POR PEDIDO DE MATÍAS, con el defecto aceptado a mano: a esa
    // altura el trazo del anillo pasa por x≈749 y la etiqueta arranca en
    // x≈730, así que el punteado le cruza las primeras letras. Es una excepción
    // deliberada a la regla del ADR de arriba —"un nodo en un flanco no lleva
    // la etiqueta centrada arriba ni abajo"— y queda registrada como tal en
    // ADR-2026-08-17-estudio-marketing-fixes-revision, que la supersede SÓLO
    // para este nodo. La regla sigue valiendo para el resto del anillo.
    destino: RUTA_PIEZA_EJEMPLO, glifo: "calidad", etiquetaEn: "abajo" },
  // "arriba": con la etiqueta abajo, el trazo del anillo le cruzaba las últimas
  // letras. Y sin `sub`: era dato muerto mientras la etiqueta iba adentro del
  // círculo, y bajo el nombre nuevo repetía lo que ya dice la etiqueta —
  // además de ser la única línea extra entre los siete nodos del anillo.
  { id: "vos", etiqueta: "Confirmación del usuario",
    rol: "humano", estado: "activo",
    posicion: { tipo: "anillo", grados: 320 }, radio: 26,
    destino: "/estudio-marketing/plan", glifo: "humano", etiquetaEn: "arriba" },

  // ── fuentes ──
  // Los tres van a `RUTA_DATOS`, no a Insights: el nodo de una fuente hace la
  // pregunta "¿esto está conectado?" y la respuesta está en la pantalla de
  // datos, no en un tablero de números. OJO: `numeros` (Métricas de tu marca)
  // SÍ sigue yendo a Insights — ahí es donde viven sus números
  // (ADR-2026-08-17-estudio-marketing-nodos-fuente-a-datos).
  // `etiquetaEn: "arriba"` POR PEDIDO EXPLÍCITO DE MATÍAS, mirando la pantalla.
  // NO REVERTIR. Una tanda de revisión ya lo devolvió a "izquierda" una vez,
  // razonando contra comentarios viejos que describían el lado anterior; el
  // pedido del usuario gana sobre la medición previa y sobre esos comentarios.
  // Medido con este lado: el texto cae en y≈58, x 380–421, y el vecino más
  // cercano es `meta_ads` (etiqueta en y≈23, x 455–492): no se tocan.
  { id: "instagram", etiqueta: "Instagram", rol: "fuente", estado: "activo",
    posicion: { tipo: "libre", x: 400, y: 86 }, radio: 14,
    destino: RUTA_DATOS, glifo: "datos", etiquetaEn: "arriba" },
  { id: "meta_ads", etiqueta: "Meta Ads", rol: "fuente", estado: "sin-conectar",
    posicion: { tipo: "libre", x: 473, y: 51 }, radio: 14,
    destino: RUTA_DATOS, glifo: "datos", etiquetaEn: "arriba" },
  { id: "tiendanube", etiqueta: "Tiendanube", rol: "fuente", estado: "activo",
    posicion: { tipo: "libre", x: 550, y: 51 }, radio: 14,
    destino: RUTA_DATOS, glifo: "datos", etiquetaEn: "derecha" },

  // ── memorias para DECIDIR (las lee el estratega) ──
  // Etiqueta "abajo" POR PEDIDO EXPLÍCITO DE MATÍAS, mirando la pantalla.
  // NO REVERTIR. Una tanda de revisión ya la devolvió a "derecha" una vez,
  // razonando contra este mismo comentario cuando describía el lado anterior.
  //
  // El defecto que trae, medido y ACEPTADO A MANO: con "abajo", la lectura
  // `perfil → estratega` —que sale del centro del nodo y baja hacia la
  // derecha— le entra por arriba a su propia etiqueta y le cruza la tinta de
  // la "c" de "marca" (caja x 220,23–283,77 / y 104,30–111; la recta pasa por
  // 272,05 / 104,30). Es el mismo tipo de excepción deliberada que la de
  // `calidad` más arriba: el pedido del usuario gana sobre la medición.
  // Si algún día molesta, los otros lados tampoco salen gratis: "arriba"
  // (y 34,3–41) se mete abajo del título del encabezado, que en el lienzo
  // angosto de 440px llega a y=44; e "izquierda" (x 160,47–224) queda a 0,47
  // unidades de la píldora "Activo". La salida limpia sería mover el nodo unas
  // unidades desde el editor visual, no cambiarle el lado.
  // La posición (252,75) es la que puso Matías a mano y no se toca.
  { id: "perfil", etiqueta: "Perfil de marca", rol: "memoria", estado: "activo",
    posicion: { tipo: "libre", x: 252, y: 75 }, radio: 20,
    destino: "/mi-marca", glifo: "memoria", etiquetaEn: "abajo" },
  { id: "personas", etiqueta: "Personas y ángulos", rol: "memoria", estado: "no-existe",
    posicion: { tipo: "libre", x: 205, y: 145 }, radio: 20,
    destino: null, glifo: "memoria", etiquetaEn: "abajo" },
  { id: "estrategia_mem", etiqueta: "Estrategia", rol: "memoria", estado: "activo",
    posicion: { tipo: "libre", x: 194, y: 222 }, radio: 20,
    destino: "/estudio-marketing/estrategia", glifo: "memoria", etiquetaEn: "abajo" },
  { id: "conocimiento", etiqueta: "Base de conocimiento", sub: "global de Atlas",
    rol: "global", estado: "no-existe",
    posicion: { tipo: "libre", x: 204, y: 300 }, radio: 20,
    destino: null, glifo: "memoria", etiquetaEn: "abajo" },

  // ── memorias para HACER. Ya no cuelgan de cada agente: las tres alimentan
  //    a `assets_marca`, que es lo único que toca el circuito. ──
  { id: "elenco", etiqueta: "Elenco de avatares", rol: "memoria", estado: "no-existe",
    posicion: { tipo: "libre", x: 364, y: 497 }, radio: 20,
    destino: null, glifo: "memoria", etiquetaEn: "abajo" },
  { id: "design_system", etiqueta: "Design system", rol: "memoria", estado: "activo",
    posicion: { tipo: "libre", x: 567, y: 519 }, radio: 20,
    destino: "/estudio-marketing/marca", glifo: "memoria", etiquetaEn: "abajo" },
  { id: "fotos", etiqueta: "Fotos de producto", rol: "memoria", estado: "activo",
    posicion: { tipo: "libre", x: 464, y: 521 }, radio: 20,
    destino: "/mi-marca", glifo: "memoria", etiquetaEn: "abajo" },
  { id: "assets_marca", etiqueta: "Assets de la marca", rol: "memoria", estado: "activo",
    posicion: { tipo: "libre", x: 477, y: 458 }, radio: 24,
    destino: "/mi-marca", glifo: "memoria", etiquetaEn: "derecha" },

  // ── fuera del camino crítico ──
  { id: "copy_canal", etiqueta: "Copy de canal", sub: "fuera del camino",
    rol: "agente", estado: "activo",
    posicion: { tipo: "libre", x: 333, y: 400 }, radio: 15,
    destino: RUTA_PIEZA_EJEMPLO, glifo: "copy", etiquetaEn: "abajo" },

  // ── todavía no existe ──
  { id: "explorador", etiqueta: "Explorador", sub: "todavía no existe",
    rol: "global", estado: "no-existe",
    posicion: { tipo: "libre", x: 239, y: 390 }, radio: 16,
    destino: null, glifo: "memoria", etiquetaEn: "abajo" },
];

export const ARISTAS: AristaGrafo[] = [
  // fuentes → tus números
  { desde: "instagram", hasta: "numeros", tipo: "flujo" },
  { desde: "meta_ads", hasta: "numeros", tipo: "flujo" },
  { desde: "tiendanube", hasta: "numeros", tipo: "flujo" },

  // el circuito
  { desde: "numeros", hasta: "estratega", tipo: "flujo" },
  { desde: "estratega", hasta: "creativo", tipo: "flujo" },
  { desde: "creativo", hasta: "dir_ia", tipo: "flujo" },
  // El orden de estas tres es el del dibujo, y tiene que seguir siéndolo: los
  // tramos entre dos nodos del anillo no se dibujan (los traza la elipse), así
  // que si el DATO dijera otra cosa nadie lo vería en pantalla y el circuito
  // mentiría para todo lo que lee la cadena — `puentear`, `recorrido`,
  // `validarGrafo` (ADR-2026-08-17-mapa-orden-y-senales).
  { desde: "dir_ia", hasta: "armado", tipo: "flujo" },
  { desde: "armado", hasta: "calidad", tipo: "flujo" },
  { desde: "calidad", hasta: "vos", tipo: "flujo" },
  { desde: "vos", hasta: "numeros", tipo: "retorno" },

  // Memorias para decidir. `personas` tiene UNA sola arista y es recta: la
  // vuelta de escritura (`estratega → personas`) se borró
  // (ADR-2026-08-17-mapa-orden-y-senales).
  { desde: "perfil", hasta: "estratega", tipo: "lectura" },
  { desde: "personas", hasta: "estratega", tipo: "lectura" },
  { desde: "estrategia_mem", hasta: "estratega", tipo: "lectura" },
  { desde: "conocimiento", hasta: "estratega", tipo: "lectura" },
  { desde: "explorador", hasta: "estratega", tipo: "lectura" },

  // memorias para hacer: las tres se juntan en `assets_marca` y ese único
  // cable entra al circuito, en el tramo de producción del anillo.
  { desde: "elenco", hasta: "assets_marca", tipo: "lectura" },
  { desde: "design_system", hasta: "assets_marca", tipo: "lectura" },
  { desde: "fotos", hasta: "assets_marca", tipo: "lectura" },
  { desde: "assets_marca", hasta: "creativo", tipo: "lectura",
    tramo: { hacia: "dir_ia", en: 0.68 } },

  // fuera del camino
  { desde: "creativo", hasta: "copy_canal", tipo: "fuera-de-camino" },
];

/**
 * ─── VARIANTES DEL CIRCUITO ────────────────────────────────────────────────
 *
 * `NODOS` y `ARISTAS` son el grafo BASE: el circuito que corre hoy con la
 * configuración por defecto. La pantalla puede apagar dos cosas
 * (`grafo/grafoSegunConfig.ts`), y cada una de esas variantes es OTRO DATO —
 * no código: describe qué nodo entra y a quiénes reemplaza. La función que las
 * aplica no conoce ningún id
 * (ADR-2026-08-17-mapa-configuracion-y-grafo-derivado).
 */
export interface VarianteGrafo {
  /** El nodo que entra. Declara su propia posición, como cualquier otro. */
  nodo: NodoGrafo;
  /**
   * Id del nodo base cuyo LUGAR ocupa: desaparece y el nuevo hereda todas sus
   * aristas (incluidas las que lo nombran en un `tramo`).
   */
  ocupa: string;
  /**
   * Otros nodos base que también se funden en el nuevo. NO hace falta que
   * sean contiguos al que ocupa: si están en el anillo, el ciclo se cierra
   * PUENTEÁNDOLOS —quien entraba a ellos pasa a apuntar a quien salía— y el
   * resto de sus aristas se reasigna al nodo nuevo. Ver `grafoSegunConfig`.
   */
  absorbe?: string[];
}

/**
 * Generación de contenido DESACTIVADA: el sistema ya no dirige a la IA ni
 * arma las piezas; entrega el plan y quien lo implementa es la marca. Los dos
 * nodos de producción (`dir_ia` y `armado`) se funden en uno solo.
 *
 * EL ÁNGULO ES EL PUNTO MEDIO DE LOS DOS (70°), no el de ninguno de ellos.
 * Desde ADR-2026-08-17-mapa-orden-y-senales `dir_ia` (90°) y `armado`
 * (50°) son VECINOS: el nodo fundido ocupa el arco de los DOS, y cualquier otro
 * ángulo lo pega contra uno de los que quedan y deja el hueco entero del otro
 * lado. Con los 90° que heredaba de `dir_ia` —correctos mientras `calidad` los
 * separaba— quedaría a 40° de `creativo` y a 80° de `calidad`: el mismo nodo
 * pisando una etapa y colgando de la otra. En 70° quedan 60 y 60.
 * Es lo primero que se rompe si mañana alguien vuelve a mover estos dos nodos.
 *
 * `ocupa`/`absorbe` NO cambian con la vecindad: el nodo nuevo sigue entrando en
 * el ÍNDICE de `dir_ia` (que es el orden de pintado y el orden en que se lee el
 * circuito) y `puentear` empalma igual de bien a dos vecinos — antes unía
 * `calidad → vos`, ahora `dir_ia → calidad`. La cadena resultante es la misma
 * que antes del cambio: `creativo → implementacion → calidad → vos`.
 *
 * Lo que SÍ se corre es dónde aterriza el cable de `assets_marca`: su `tramo`
 * va de `creativo` (130°) al nodo de producción, así que la fracción 0.68 cae
 * en 89.2° en vez de 102.8°. Sigue siendo el tramo de producción del anillo,
 * que es lo que esa arista quiere decir.
 */
export const VARIANTE_SIN_GENERACION: VarianteGrafo = {
  ocupa: "dir_ia",
  absorbe: ["armado"],
  nodo: {
    id: "implementacion",
    etiqueta: "Implementación del plan",
    rol: "agente",
    estado: "activo",
    posicion: { tipo: "anillo", grados: 70 },
    radio: 26,
    destino: "/estudio-marketing/plan",
    // Hereda el glifo del nodo que absorbe: implementar es armar entregables,
    // y el de `dir_ia` (el triángulo de la IA) sería mentira justo en el modo
    // en el que la IA no genera nada.
    glifo: "armado",
    etiquetaEn: "abajo",
  },
};

/**
 * Publicación automática ACTIVADA: ya no hay una persona que confirme. El
 * nodo deja de ser `humano` y pasa a ser del sistema —rol `agente` y glifo
 * propio— en la MISMA posición de `vos` (320°), porque es la misma etapa del
 * ciclo: lo último antes de que las métricas vuelvan a entrar.
 */
export const VARIANTE_PUBLICACION_AUTOMATICA: VarianteGrafo = {
  ocupa: "vos",
  nodo: {
    id: "publicacion_auto",
    etiqueta: "Publicación automática",
    rol: "agente",
    estado: "activo",
    posicion: { tipo: "anillo", grados: 320 },
    radio: 26,
    destino: "/estudio-marketing/plan",
    glifo: "publicacion",
    etiquetaEn: "arriba",
  },
};

export function validarGrafo(nodos: NodoGrafo[], aristas: AristaGrafo[]): string[] {
  const errores: string[] = [];
  const ids = new Set<string>();
  const porId = new Map<string, NodoGrafo>();
  for (const nodo of nodos) {
    if (ids.has(nodo.id)) errores.push(`id repetido: ${nodo.id}`);
    ids.add(nodo.id);
    porId.set(nodo.id, nodo);
    if (nodo.estado === "no-existe" && nodo.destino !== null) {
      errores.push(`${nodo.id}: no existe pero tiene destino`);
    }
    if (nodo.destino && !RUTAS_VALIDAS.includes(nodo.destino)) {
      errores.push(`${nodo.id}: destino desconocido ${nodo.destino}`);
    }
  }
  const enAnillo = (id: string) => porId.get(id)?.posicion.tipo === "anillo";
  for (const arista of aristas) {
    if (!ids.has(arista.desde)) errores.push(`arista desde nodo inexistente: ${arista.desde}`);
    if (!ids.has(arista.hasta)) errores.push(`arista hacia nodo inexistente: ${arista.hasta}`);
    if (!arista.tramo) continue;
    // Una arista con `tramo` cae sobre el arco del anillo: sin los dos
    // extremos ahí no hay arco que interpolar.
    const { hacia, en } = arista.tramo;
    if (!ids.has(hacia)) {
      errores.push(`arista hacia nodo inexistente: ${hacia}`);
    } else if (!enAnillo(hacia)) {
      errores.push(`tramo ${arista.hasta}→${hacia}: ${hacia} no está en el anillo`);
    }
    if (ids.has(arista.hasta) && !enAnillo(arista.hasta)) {
      errores.push(`tramo ${arista.hasta}→${hacia}: ${arista.hasta} no está en el anillo`);
    }
    if (!(en >= 0 && en <= 1)) {
      errores.push(`tramo ${arista.hasta}→${hacia}: fracción fuera de rango (${en})`);
    }
  }
  return errores;
}
